import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const ProfileSetupModal = ({ user, onComplete }) => {
  const [name, setName] = useState(user?.name || '');
  const [timezone, setTimezone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-detect timezone
  useEffect(() => {
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimezone(detectedTimezone);
    } catch (err) {
      setTimezone('America/New_York');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`${API}/me`, { name, timezone });
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const timezones = [
    { label: 'Auto-detected', value: Intl.DateTimeFormat().resolvedOptions().timeZone },
    { label: '--- Africa ---', value: '', disabled: true },
    { label: 'Harare (UTC+2)', value: 'Africa/Harare' },
    { label: 'Johannesburg (UTC+2)', value: 'Africa/Johannesburg' },
    { label: 'Lagos (UTC+1)', value: 'Africa/Lagos' },
    { label: 'Cairo (UTC+2)', value: 'Africa/Cairo' },
    { label: '--- Americas ---', value: '', disabled: true },
    { label: 'New York (UTC-5)', value: 'America/New_York' },
    { label: 'Chicago (UTC-6)', value: 'America/Chicago' },
    { label: 'Denver (UTC-7)', value: 'America/Denver' },
    { label: 'Los Angeles (UTC-8)', value: 'America/Los_Angeles' },
    { label: '--- Europe ---', value: '', disabled: true },
    { label: 'London (UTC+0)', value: 'Europe/London' },
    { label: 'Paris (UTC+1)', value: 'Europe/Paris' },
    { label: '--- Asia/Pacific ---', value: '', disabled: true },
    { label: 'Dubai (UTC+4)', value: 'Asia/Dubai' },
    { label: 'Singapore (UTC+8)', value: 'Asia/Singapore' },
    { label: 'Sydney (UTC+11)', value: 'Australia/Sydney' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚙️</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Profile</h2>
          <p className="text-gray-600">We need your timezone to send reminders at the right time.</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            >
              {timezones.map((tz, index) => (
                <option key={index} value={tz.value} disabled={tz.disabled}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileSetupModal;
